import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ReportDetailModal from './ReportDetailModal';

const ReportedProfilesTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);

  const mockReports = [
    {
      id: 'RPT001',
      reportedUserId: 'USR12345',
      reportedUserName: 'Anonymous User #12345',
      reporterCount: 3,
      reason: 'Inappropriate behavior',
      status: 'pending',
      severity: 'high',
      reportDate: '2026-01-25T10:30:00',
      lastUpdated: '2026-01-27T14:20:00',
      description: 'Multiple users reported inappropriate messages and harassment'
    },
    {
      id: 'RPT002',
      reportedUserId: 'USR67890',
      reportedUserName: 'Anonymous User #67890',
      reporterCount: 1,
      reason: 'Fake profile',
      status: 'under_review',
      severity: 'medium',
      reportDate: '2026-01-26T15:45:00',
      lastUpdated: '2026-01-28T09:10:00',
      description: 'Suspected fake profile with stolen photos'
    },
    {
      id: 'RPT003',
      reportedUserId: 'USR11223',
      reportedUserName: 'Anonymous User #11223',
      reporterCount: 5,
      reason: 'Spam/Scam',
      status: 'resolved',
      severity: 'high',
      reportDate: '2026-01-20T08:15:00',
      lastUpdated: '2026-01-24T16:30:00',
      description: 'User was sending spam messages requesting money'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'under_review':
        return 'text-primary bg-primary/10';
      case 'resolved':
        return 'text-success bg-success/10';
      case 'dismissed':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-error bg-error/10';
      case 'medium':
        return 'text-warning bg-warning/10';
      case 'low':
        return 'text-success bg-success/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filteredReports = mockReports?.filter((report) => {
    const matchesSearch = report?.reportedUserName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         report?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || report?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Search by report ID or user name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
          />
        </div>
        <div className="flex gap-2">
          {['all', 'pending', 'under_review', 'resolved']?.map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-2 rounded-lg font-caption font-medium text-sm transition-smooth ${
                filterStatus === status
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + status?.replace('_', ' ')?.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card rounded-xl shadow-warm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">Report ID</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">User</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">Reason</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">Reports</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">Severity</th>
                <th className="text-left p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">Status</th>
                <th className="text-right p-3 md:p-4 text-xs md:text-sm font-caption font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports?.map((report) => (
                <tr key={report?.id} className="border-b border-border hover:bg-muted/20 transition-smooth">
                  <td className="p-3 md:p-4 text-xs md:text-sm font-mono font-medium">
                    {report?.id}
                  </td>
                  <td className="p-3 md:p-4 text-xs md:text-sm">
                    <div>
                      <p className="font-medium">{report?.reportedUserName}</p>
                      <p className="text-xs text-muted-foreground">{report?.reportedUserId}</p>
                    </div>
                  </td>
                  <td className="p-3 md:p-4 text-xs md:text-sm">
                    {report?.reason}
                  </td>
                  <td className="p-3 md:p-4 text-xs md:text-sm">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-full font-medium">
                      <Icon name="Users" size={12} />
                      {report?.reporterCount}
                    </span>
                  </td>
                  <td className="p-3 md:p-4">
                    <span className={`inline-flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs font-medium ${getSeverityColor(report?.severity)}`}>
                      <Icon name="AlertCircle" size={12} />
                      {report?.severity?.charAt(0)?.toUpperCase() + report?.severity?.slice(1)}
                    </span>
                  </td>
                  <td className="p-3 md:p-4">
                    <span className={`inline-flex items-center gap-1.5 px-2 md:px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(report?.status)}`}>
                      {report?.status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + report?.status?.replace('_', ' ')?.slice(1)}
                    </span>
                  </td>
                  <td className="p-3 md:p-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={() => setSelectedReport(report)}
                    >
                      <span className="hidden md:inline">Review</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredReports?.length === 0 && (
          <div className="p-8 md:p-12 text-center">
            <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm md:text-base text-muted-foreground mb-1">No reports found</p>
            <p className="text-xs md:text-sm text-muted-foreground">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {selectedReport && (
        <ReportDetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
};

export default ReportedProfilesTab;